import React from "react";
import { Info } from "lucide-react";

import {
  MarketDataResolveDialogField,
  MarketDataResolveDialogFooter,
  MarketDataResolveDialogShell,
} from "@/components/MarketData/MarketDataResolveDialogShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { MARKET_DATA_RESOLVE_DIALOG_COPY } from "@/constants/marketDataResolveDialogCopy";
import { BROKER_FIELD_DESC_CLASS } from "@/components/Broker/brokerAlertUi";
import { cn } from "@/lib/utils";
import type { MarketDataAttribution } from "../../../types/marketDataAttribution";
import type { MarketDataAssetClassRequiredBody } from "../../../types/instruments";
import type { MarketDataSourceOption } from "../../../types/marketDataSources";
import {
  resolveLayer2ConsentLabel,
  resolveSourcePickerCaveat,
} from "../../../utils/marketDataAttributionCopy";
import {
  defaultMarketDataSourceOption,
  formatMarketDataSourceSelectLabel,
  formatMarketDataSourceOptionLabel,
  marketDataBrokerKey,
  resolveMarketDataSourceOptions,
} from "../../../utils/marketDataSourceChoice";
import { formatAssetClassLabel } from "../../../utils/tradingViewBroker";
import {
  normalizeQuoteVariantCandidates,
  qualifiedQuoteVariantForResolve,
  quoteVariantSelectOptions,
  resolveInitialQuoteVariant,
  isQuoteVariantOhlcSupported,
} from "../../../utils/instrumentSetupSave";
import { AccountSettingsFieldSelect } from "@/views/Dashboard/Profile/components/AccountSettingsFieldSelect";

function marketDataSourceSelectOptions(sources: MarketDataSourceOption[]) {
  return sources.map((source, index) => ({
    value: String(index),
    label: formatMarketDataSourceSelectLabel(source),
  }));
}

function countCoinApiSources(sources: MarketDataSourceOption[]): number {
  return sources.filter((s) => marketDataBrokerKey(s) === "coinapi").length;
}

function MarketDataSourcePickerCaveat(props: {
  source: MarketDataSourceOption | undefined;
  onConnectBroker?: (brokerSlug: string) => void;
}): React.ReactElement | null {
  const broker = marketDataBrokerKey(props.source ?? {});
  const caveat = resolveSourcePickerCaveat(props.source);
  const isByokBroker = broker === "ctrader" || broker === "liquid" || broker === "liquidbrokers";

  if (!caveat && !isByokBroker) return null;

  return (
    <div className="mt-3 space-y-2">
      {caveat ? (
        <Alert variant="info" className="font-[family-name:var(--font-primary)] text-sm font-normal">
          <Info aria-hidden />
          <AlertDescription className={BROKER_FIELD_DESC_CLASS}>{caveat}</AlertDescription>
        </Alert>
      ) : null}
      {isByokBroker && props.onConnectBroker ? (
        <Button
          type="button"
          variant="link"
          size="sm"
          data-font="primary"
          className="h-auto p-0 text-[color:var(--brand-tropical-surf)]"
          onClick={() =>
            props.onConnectBroker?.(broker === "ctrader" ? "ctrader" : "liquidbrokers")
          }
        >
          {broker === "ctrader" ? "Connect cTrader OAuth" : "Connect Liquid Brokers"}
        </Button>
      ) : null}
    </div>
  );
}

export function MarketDataSourceChoiceModal(props: {
  isOpen: boolean;
  onClose: () => void;
  symbol: string;
  sources: MarketDataSourceOption[];
  onConfirm: (source: MarketDataSourceOption) => void | Promise<void>;
  saving?: boolean;
  onConnectBroker?: (brokerSlug: string) => void;
}): React.ReactElement | null {
  const defaultSource = defaultMarketDataSourceOption(props.sources);
  const defaultIndex = defaultSource ? Math.max(0, props.sources.indexOf(defaultSource)) : 0;

  const [selectedIndex, setSelectedIndex] = React.useState(String(defaultIndex));

  React.useEffect(() => {
    if (!props.isOpen) return;
    setSelectedIndex(String(defaultIndex));
  }, [props.isOpen, defaultIndex]);

  if (!props.isOpen || props.sources.length === 0) return null;

  const selectedSource = props.sources[Number(selectedIndex)] ?? props.sources[0];
  const coinApiCount = countCoinApiSources(props.sources);

  return (
    <MarketDataResolveDialogShell
      open={props.isOpen}
      onOpenChange={(open) => !open && props.onClose()}
      size="lg"
      title={`Market data for ${props.symbol}`}
      description="Choose which OHLC listing to use for charting and report analysis. Your choice is saved for this trade account."
      footer={
        <MarketDataResolveDialogFooter
          onCancel={props.onClose}
          onConfirm={() => selectedSource && void props.onConfirm(selectedSource)}
          confirmLabel={MARKET_DATA_RESOLVE_DIALOG_COPY.confirmMarketDataSource}
          confirmDisabled={!selectedSource}
          loading={props.saving}
        />
      }
    >
      {coinApiCount > 1 ? (
        <p className={cn(BROKER_FIELD_DESC_CLASS, "mb-4")}>
          {coinApiCount} CoinAPI exchange listings are available — pick the venue that best matches
          where you traded.
        </p>
      ) : null}
      <MarketDataResolveDialogField
        label="OHLC source"
        description="Saved for this symbol on your trade account."
        htmlFor="market-data-source-choice"
      >
        <AccountSettingsFieldSelect
          id="market-data-source-choice"
          value={selectedIndex}
          onValueChange={setSelectedIndex}
          options={marketDataSourceSelectOptions(props.sources)}
          placeholder="Select listing"
        />
      </MarketDataResolveDialogField>
      <MarketDataSourcePickerCaveat
        source={selectedSource}
        onConnectBroker={props.onConnectBroker}
      />
    </MarketDataResolveDialogShell>
  );
}

export function MarketDataAssetClassRequiredModal(props: {
  isOpen: boolean;
  onClose: () => void;
  payload: MarketDataAssetClassRequiredBody;
  onConfirm: (selection: {
    assetClass: string;
    marketDataBroker?: string;
    marketDataSource?: MarketDataSourceOption;
  }) => void | Promise<void>;
  saving?: boolean;
}): React.ReactElement | null {
  const options = props.payload.valid_asset_classes ?? [];
  const marketDataSources = resolveMarketDataSourceOptions({
    market_data_sources: props.payload.market_data_sources,
    candidate_brokers: props.payload.candidate_brokers,
  });
  const showListingPicker = marketDataSources.length > 1;
  const brokerOptions = props.payload.candidate_brokers ?? [];
  const defaultAssetClass =
    props.payload.suggested_asset_class?.trim() || options[0] || "";
  const defaultSource = defaultMarketDataSourceOption(marketDataSources);
  const defaultSourceIndex = defaultSource
    ? Math.max(0, marketDataSources.indexOf(defaultSource))
    : 0;
  const defaultBroker = brokerOptions[0]?.broker ?? "";

  const [selectedAssetClass, setSelectedAssetClass] = React.useState(defaultAssetClass);
  const [selectedBroker, setSelectedBroker] = React.useState(defaultBroker);
  const [selectedSourceIndex, setSelectedSourceIndex] = React.useState(String(defaultSourceIndex));

  React.useEffect(() => {
    if (!props.isOpen) return;
    setSelectedAssetClass(defaultAssetClass);
    setSelectedBroker(defaultBroker);
    setSelectedSourceIndex(String(defaultSourceIndex));
  }, [props.isOpen, defaultAssetClass, defaultBroker, defaultSourceIndex]);

  if (!props.isOpen) return null;

  return (
    <MarketDataResolveDialogShell
      open={props.isOpen}
      onOpenChange={(open) => !open && props.onClose()}
      title={`Market data for ${props.payload.symbol}`}
      description="Choose how to load OHLC for this symbol. Your choice is saved for this account and used on retry."
      footer={
        <MarketDataResolveDialogFooter
          onCancel={props.onClose}
          confirmLabel={MARKET_DATA_RESOLVE_DIALOG_COPY.saveAndContinue}
          confirmDisabled={!selectedAssetClass}
          loading={props.saving}
          onConfirm={() => {
            const listingSource = showListingPicker
              ? marketDataSources[Number(selectedSourceIndex)]
              : undefined;
            void props.onConfirm({
              assetClass: selectedAssetClass,
              marketDataBroker:
                (listingSource?.market_data_broker ??
                  listingSource?.broker ??
                  selectedBroker) ||
                undefined,
              marketDataSource: listingSource,
            });
          }}
        />
      }
    >
      <div className="flex flex-col gap-4">
        <MarketDataResolveDialogField
          label="Asset class"
          htmlFor="market-data-asset-class"
        >
          <AccountSettingsFieldSelect
            id="market-data-asset-class"
            value={selectedAssetClass}
            onValueChange={setSelectedAssetClass}
            options={
              options.length > 0
                ? options.map((o) => ({ value: o, label: formatAssetClassLabel(o) }))
                : defaultAssetClass
                  ? [{ value: defaultAssetClass, label: formatAssetClassLabel(defaultAssetClass) }]
                  : []
            }
            placeholder="Select class"
          />
        </MarketDataResolveDialogField>
        {showListingPicker ? (
          <MarketDataResolveDialogField label="OHLC source" htmlFor="market-data-listing">
            <AccountSettingsFieldSelect
              id="market-data-listing"
              value={selectedSourceIndex}
              onValueChange={setSelectedSourceIndex}
              options={marketDataSourceSelectOptions(marketDataSources)}
              placeholder="Select listing"
            />
            <MarketDataSourcePickerCaveat
              source={marketDataSources[Number(selectedSourceIndex)]}
            />
          </MarketDataResolveDialogField>
        ) : brokerOptions.length > 0 ? (
          <MarketDataResolveDialogField label="OHLC provider" htmlFor="market-data-broker">
            <AccountSettingsFieldSelect
              id="market-data-broker"
              value={selectedBroker}
              onValueChange={setSelectedBroker}
              options={brokerOptions.map((c) => ({
                value: c.broker,
                label: c.reason ? `${c.broker} — ${c.reason}` : c.broker,
              }))}
              placeholder="Select provider"
            />
          </MarketDataResolveDialogField>
        ) : null}
      </div>
    </MarketDataResolveDialogShell>
  );
}

export function InstrumentAssetClassModal(props: {
  isOpen: boolean;
  onClose: () => void;
  symbol: string;
  options: string[];
  onConfirm: (assetClass: string) => void;
}): React.ReactElement | null {
  const [selected, setSelected] = React.useState(props.options[0] ?? "");

  React.useEffect(() => {
    if (props.isOpen && props.options.length > 0) {
      setSelected(props.options[0]);
    }
  }, [props.isOpen, props.options]);

  if (!props.isOpen) return null;

  return (
    <MarketDataResolveDialogShell
      open={props.isOpen}
      onOpenChange={(open) => !open && props.onClose()}
      title={`Confirm asset class for ${props.symbol}`}
      description={`${props.symbol} matches multiple asset classes. Choose one to continue loading OHLC.`}
      footer={
        <MarketDataResolveDialogFooter
          onCancel={props.onClose}
          confirmLabel={MARKET_DATA_RESOLVE_DIALOG_COPY.saveAndContinue}
          confirmDisabled={!selected}
          onConfirm={() => props.onConfirm(selected)}
        />
      }
    >
      <MarketDataResolveDialogField label="Asset class" htmlFor="instrument-asset-class">
        <AccountSettingsFieldSelect
          id="instrument-asset-class"
          value={selected}
          onValueChange={setSelected}
          options={props.options.map((o) => ({
            value: o,
            label: formatAssetClassLabel(o),
          }))}
          placeholder="Select class"
        />
      </MarketDataResolveDialogField>
    </MarketDataResolveDialogShell>
  );
}

export function MarketDataQuoteVariantModal(props: {
  isOpen: boolean;
  onClose: () => void;
  tradeSymbol: string;
  candidates: Array<{
    symbol: string;
    display_symbol?: string;
    instrument_id?: string;
    description?: string;
    picker_label?: string;
    market_kind?: string;
    market_data_broker?: string;
    market_data_broker_display?: string;
  }>;
  suggested?: string | null;
  autoResolved?: string | null;
  onConfirm: (qualifiedSymbol: string) => void | Promise<void>;
  saving?: boolean;
}): React.ReactElement | null {
  const normalized = normalizeQuoteVariantCandidates(props.candidates);
  const menuOptions = quoteVariantSelectOptions(normalized);
  const defaultSymbol = resolveInitialQuoteVariant(
    props.suggested,
    normalized,
    props.autoResolved
  );
  const [selected, setSelected] = React.useState(defaultSymbol);

  React.useEffect(() => {
    if (!props.isOpen) return;
    setSelected(defaultSymbol);
  }, [props.isOpen, defaultSymbol]);

  if (!props.isOpen || menuOptions.length === 0) return null;

  const selectedCandidate = normalized.find(
    (c) => c.symbol.trim() === selected || c.display_symbol?.trim() === selected
  );
  const selectedSupported =
    !selected || !selectedCandidate || isQuoteVariantOhlcSupported(selectedCandidate);

  return (
    <MarketDataResolveDialogShell
      open={props.isOpen}
      onOpenChange={(open) => !open && props.onClose()}
      size="lg"
      title={`Quote variant for ${props.tradeSymbol}`}
      description={
        <>
          Choose which market data symbol to use for OHLC on trades labeled{" "}
          <span className="font-medium text-foreground">{props.tradeSymbol}</span>. Your choice is
          saved for this trade account.
        </>
      }
      footer={
        <MarketDataResolveDialogFooter
          onCancel={props.onClose}
          confirmLabel={MARKET_DATA_RESOLVE_DIALOG_COPY.confirmQuoteVariant}
          confirmDisabled={!selected || !selectedSupported}
          loading={props.saving}
          onConfirm={() => {
            if (!selected || !selectedSupported) return;
            void props.onConfirm(qualifiedQuoteVariantForResolve(normalized, selected));
          }}
        />
      }
    >
      <MarketDataResolveDialogField
        label="Quote variant"
        description="Pick the listing that matches where you traded."
        htmlFor="market-data-quote-variant"
      >
        <AccountSettingsFieldSelect
          id="market-data-quote-variant"
          value={selected}
          onValueChange={setSelected}
          options={menuOptions}
          placeholder="Select variant"
        />
      </MarketDataResolveDialogField>
    </MarketDataResolveDialogShell>
  );
}

export function InstrumentConsentModal(props: {
  isOpen: boolean;
  onClose: () => void;
  symbol: string;
  onGrant: () => void | Promise<void>;
  granting?: boolean;
  attribution?: MarketDataAttribution | null;
  source?: MarketDataSourceOption | null;
}): React.ReactElement | null {
  const [consentChecked, setConsentChecked] = React.useState(false);

  React.useEffect(() => {
    if (!props.isOpen) return;
    setConsentChecked(false);
  }, [props.isOpen, props.symbol]);

  if (!props.isOpen) return null;

  const consentLabel = resolveLayer2ConsentLabel({
    symbol: props.symbol,
    attribution: props.attribution,
    source: props.source,
  });

  return (
    <MarketDataResolveDialogShell
      open={props.isOpen}
      onOpenChange={(open) => !open && props.onClose()}
      title={`Market data for ${props.symbol}`}
      description="Confirm how OHLC will be loaded for this symbol on your trade account before charts and reports can run."
      footer={
        <MarketDataResolveDialogFooter
          onCancel={props.onClose}
          confirmLabel={MARKET_DATA_RESOLVE_DIALOG_COPY.confirmMarketDataSource}
          confirmDisabled={!consentChecked}
          loading={props.granting}
          onConfirm={() => void props.onGrant()}
        />
      }
    >
      <Label
        htmlFor="market-data-layer2-consent"
        className="flex cursor-pointer items-start gap-3 font-normal"
      >
        <Checkbox
          id="market-data-layer2-consent"
          checked={consentChecked}
          onCheckedChange={(value) => setConsentChecked(value === true)}
          className="mt-0.5"
        />
        <span className={cn(BROKER_FIELD_DESC_CLASS, "leading-5")}>{consentLabel}</span>
      </Label>
    </MarketDataResolveDialogShell>
  );
}
